import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { addHighScore, getHighScoreSet, qualifiesForHighScore, submitGlobalHighScore, syncGlobalHighScores } from '../utils/highScore';
import { showSimpleNotification } from '../utils/notifications';
import { trackEvent } from '../utils/umami';
import { useT } from '../i18n';
import { HighScore, HighScoreScope } from '../types/game';
import { HighlightItem } from './HighlightsPanel';

interface GameOverModalProps {
  isVisible: boolean;
  totalScore: number;
  onNewGame: () => void;
  onViewHighScores: () => void;
  winnerName?: string;
  modeId?: string;
  highlights?: HighlightItem[];
}

type GameOverTab = 'result' | 'highlights' | 'highscores';

const Overlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: flex-start;
  z-index: 1000;
  padding: 12px;
  box-sizing: border-box;
  overflow-y: auto;
  padding-top: max(12px, calc(50vh - 300px));
  
  @media (max-height: 600px) {
    align-items: flex-start;
    padding-top: 12px;
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 520px;
  width: 100%;
  text-align: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  min-height: auto;
  margin: 20px auto;
  
  @media (max-height: 600px) {
    max-height: auto;
    margin: 10px auto;
  }
`;

const ModalContent = styled.div`
  padding: 30px 28px 0 28px;
  overflow-y: auto;
  flex: 1;
  
  @media (max-height: 800px) {
    padding: 30px 30px 0 30px;
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px 24px;
  flex-shrink: 0;
  border-top: 1px solid #f0f0f0;
  
  @media (max-height: 800px) {
    padding: 15px 30px 30px 30px;
  }
`;

const Title = styled.h2`
  color: #333;
  font-size: 28px;
  margin-bottom: 14px;
`;

const ScoreDisplay = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #4CAF50;
  margin: 20px 0;
`;

const Congratulations = styled.div`
  font-size: 18px;
  color: #FF5722;
  margin-bottom: 14px;
  font-weight: bold;
`;

const ScopePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e3f2fd;
  color: #0d47a1;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 8px 0 18px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${p => p.$active ? '#1976d2' : '#d6d6d6'};
  background: ${p => p.$active ? '#e3f2fd' : '#fafafa'};
  color: ${p => p.$active ? '#0d47a1' : '#555'};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 8px;
  cursor: pointer;
`;

const TabPanel = styled.div`
  text-align: left;
  margin-top: 6px;
`;

const PositionHint = styled.div`
  background: #fff3e0;
  border-left: 4px solid #fb8c00;
  border-radius: 8px;
  padding: 10px 12px;
  color: #7a4a00;
  font-size: 14px;
  margin: 12px 0;
`;

const TopThreeWrap = styled.div`
  background: #f8f9fb;
  border-radius: 12px;
  border: 1px solid #eceff3;
  padding: 12px;
  margin-top: 10px;
`;

const TopThreeTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
`;

const TopThreeItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const RankBadge = styled.span`
  font-weight: 800;
  color: #374151;
  min-width: 34px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 10px;
  color: #666;
  font-style: italic;
`;

const HighlightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HighlightRow = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 10px;
  align-items: center;
  background: #f5f5f5;
  border-radius: 10px;
  padding: 10px;
`;

const HighlightText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScoreScopeTabs = styled.div`
  display: inline-flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ScopeTabButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${p => p.$active ? '#1e88e5' : '#cfd8dc'};
  background: ${p => p.$active ? '#e1f5fe' : 'white'};
  color: ${p => p.$active ? '#0d47a1' : '#455a64'};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  padding: 7px 14px;
  cursor: pointer;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  background: ${props => props.$variant === 'primary' ? '#4CAF50' : '#2196F3'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${props => props.$variant === 'primary' ? '#a9d6a5' : '#90caf9'};
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'primary' ? '#45a049' : '#1976D2'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    padding: 14px 24px;
    font-size: 17px;
    border-radius: 10px;
    min-width: 140px;
  }
`;

const GameOverModal: React.FC<GameOverModalProps> = ({
  isVisible,
  totalScore,
  onNewGame,
  onViewHighScores,
  winnerName,
  modeId = 'classic',
  highlights = []
}) => {
  const t = useT();
  const [playerName, setPlayerName] = useState('');
  const [hasSubmittedScore, setHasSubmittedScore] = useState(false);
  const [activeTab, setActiveTab] = useState<GameOverTab>('result');
  const [scoreScope, setScoreScope] = useState<HighScoreScope>('local');
  const [highScoreSet, setHighScoreSet] = useState(() => getHighScoreSet(modeId));
  const [isSyncingGlobal, setIsSyncingGlobal] = useState(false);
  const [isSavingScore, setIsSavingScore] = useState(false);

  const isLocalHighScore = useMemo(() => {
    return qualifiesForHighScore(totalScore, highScoreSet.local, modeId, 'local');
  }, [highScoreSet.local, modeId, totalScore]);

  const isGlobalHighScore = useMemo(() => {
    return qualifiesForHighScore(totalScore, highScoreSet.global, modeId, 'global');
  }, [highScoreSet.global, modeId, totalScore]);
  const isNewHighScore = isLocalHighScore || isGlobalHighScore;

  const scopesToSave = useMemo<HighScoreScope[]>(() => {
    const scopes: HighScoreScope[] = [];
    if (isLocalHighScore) scopes.push('local');
    if (isGlobalHighScore) scopes.push('global');
    return scopes;
  }, [isLocalHighScore, isGlobalHighScore]);

  const isReverseMode = modeId === 'reverse';

  const scoreList = scoreScope === 'global' ? highScoreSet.global : highScoreSet.local;
  const top3Local = highScoreSet.local.slice(0, 3);

  const getProjectedRank = React.useCallback((scores: HighScore[]) => {
    if (scores.length === 0) return 1;
    const idx = scores.findIndex(entry => {
      if (isReverseMode) return totalScore < entry.score;
      return totalScore > entry.score;
    });
    return idx === -1 ? scores.length + 1 : idx + 1;
  }, [isReverseMode, totalScore]);

  const projectedLocalRank = useMemo(() => {
    return getProjectedRank(highScoreSet.local);
  }, [getProjectedRank, highScoreSet.local]);

  const projectedGlobalRank = useMemo(() => {
    return getProjectedRank(highScoreSet.global);
  }, [getProjectedRank, highScoreSet.global]);
  
  useEffect(() => {
    if (isVisible) {
      setActiveTab('result');
      setScoreScope('local');
      setHighScoreSet(getHighScoreSet(modeId));
    }
  }, [isVisible, modeId]);

  useEffect(() => {
    let cancelled = false;

    if (!isVisible) return;

    setIsSyncingGlobal(true);
    syncGlobalHighScores(modeId)
      .then(set => {
        if (!cancelled) {
          setHighScoreSet(set);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSyncingGlobal(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isVisible, modeId]);

  useEffect(() => {
    if (winnerName) {
      setPlayerName(winnerName);
      // Automatiskt spara highscore för multiplayer-spelare
      if (isNewHighScore && !hasSubmittedScore) {
        const timer = setTimeout(() => {
          const saveWinnerScore = async () => {
            if (scopesToSave.length === 0) return;
            if (scopesToSave.includes('local')) {
              const nextSet = addHighScore(winnerName, totalScore, modeId, 'local');
              setHighScoreSet(prev => ({ ...prev, local: nextSet.local, updatedAt: nextSet.updatedAt }));
            }
            if (scopesToSave.includes('global')) {
              const nextSet = await submitGlobalHighScore(winnerName, totalScore, modeId);
              setHighScoreSet(nextSet);
            }
            setHasSubmittedScore(true);
            try { localStorage.setItem('yatzy-last-name', winnerName); } catch {}
          };

          void saveWinnerScore();
        }, 500);
        return () => clearTimeout(timer);
      }
      return;
    }
    try {
      const last = localStorage.getItem('yatzy-last-name');
      if (last) setPlayerName(last);
    } catch {}
  }, [winnerName, isNewHighScore, hasSubmittedScore, scopesToSave, totalScore, modeId]);

  const highScoreMessage = useMemo(() => {
    if (scopesToSave.length === 2) return '🏆 Nytt rekord i både lokal och global topplista!';
    if (scopesToSave[0] === 'global') return '🌍 Nytt globalt rekord! Snyggt spelat!';
    if (scopesToSave[0] === 'local') return '🏆 Nytt lokalt rekord! Grattis!';
    return '🏆 Nytt rekord! Grattis!';
  }, [scopesToSave]);

  const scopeSummary = useMemo(() => {
    if (scopesToSave.length === 2) return 'Sparas i både lokal och global topplista';
    if (scopesToSave[0] === 'global') return 'Sparas i den globala topplistan';
    if (scopesToSave[0] === 'local') return 'Sparas i den lokala topplistan';
    return '';
  }, [scopesToSave]);

  const handleSubmitScore = async () => {
    if (isNewHighScore && !hasSubmittedScore) {
      const nameToUse = (winnerName || playerName).trim() || 'Anonym';
      setIsSavingScore(true);
      if (scopesToSave.includes('local')) {
        const nextSet = addHighScore(nameToUse, totalScore, modeId, 'local');
        setHighScoreSet(prev => ({ ...prev, local: nextSet.local, updatedAt: nextSet.updatedAt }));
      }
      if (scopesToSave.includes('global')) {
        const nextSet = await submitGlobalHighScore(nameToUse, totalScore, modeId);
        setHighScoreSet(nextSet);
      }
      try { localStorage.setItem('yatzy-last-name', nameToUse); } catch {}
      trackEvent('highscore-saved', { mode: modeId, score: totalScore, scope: scopesToSave.join('+') });
      try {
        const scopeText = scopesToSave.length === 2
          ? 'lokala och globala listorna'
          : scopesToSave[0] === 'global'
            ? 'den globala listan'
            : 'den lokala listan';
        showSimpleNotification(
          '🏆 Ny highscore!',
          `${nameToUse} fick ${totalScore} poäng${modeId ? ` (${modeId})` : ''} – plats på ${scopeText}.`,
          'highscore'
        );
      } catch {}
      setHasSubmittedScore(true);
      setIsSavingScore(false);
    }
  };

  // CHANGE (2026-08-15): Dela resultat via Web Share API (mobil) med
  // urklipps-fallback (desktop). Texten byggs av poäng + läge + höjdpunkter.
  const [shareCopied, setShareCopied] = useState(false);
  const shareModeLabel = useMemo(() => {
    if (!modeId) return '';
    if (modeId.startsWith('daily:')) return ' i Dagens utmaning';
    const names: Record<string, string> = {
      classic: '', maxi: ' i Maxi Yatzy', 'forced-classic': ' i Tvingande',
      'forced-maxi': ' i Tvingande Maxi', speed: ' i Speed Yatzy',
      reverse: ' i Reverse Yatzy', blitz: ' i Blitz Mode', 'best-of-3': ' i Best of 3',
    };
    return names[modeId] ?? '';
  }, [modeId]);

  const handleShare = async () => {
    const parts = [`🎲 Jag fick ${totalScore} poäng${shareModeLabel} i Yatzy!`];
    if (highlights.some(h => h.title === 'Yatzy!')) parts.push('🎯 Yatzy!');
    if (highlights.some(h => h.title === 'Stor stege')) parts.push('🪜 Stor stege!');
    parts.push('Kan du slå mig?');
    const text = parts.join(' ');
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Yatzy', text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
      trackEvent('result-shared', { mode: modeId, score: totalScore });
    } catch {
      // Avbruten delning (t.ex. stängd share-dialog) är inget fel.
    }
  };

  const handleNewGame = () => {
    // Från GameOver-modal är nytt spel naturligt - ingen bekräftelse behövs
    setPlayerName('');
    setHasSubmittedScore(false);
    setActiveTab('result');
    onNewGame();
  };

  const handleViewHighScores = async () => {
    if (isNewHighScore && !hasSubmittedScore) {
      await handleSubmitScore();
    }
    onViewHighScores();
  };

  if (!isVisible) return null;

  return (
    <Overlay $isVisible={isVisible}>
      <ModalContainer>
        <ModalContent>
          <Title>{winnerName ? t('over.winner', { name: winnerName }) : t('over.done')}</Title>

          <Tabs>
            <TabButton $active={activeTab === 'result'} onClick={() => setActiveTab('result')}>{t('over.tab.result')}</TabButton>
            <TabButton $active={activeTab === 'highlights'} onClick={() => setActiveTab('highlights')}>{t('over.tab.highlights')}</TabButton>
            <TabButton $active={activeTab === 'highscores'} onClick={() => setActiveTab('highscores')}>{t('over.tab.highscores')}</TabButton>
          </Tabs>
          
          {activeTab === 'result' && (
            <TabPanel>
              <div style={{ textAlign: 'center' }}>
                <ScoreDisplay>{totalScore}</ScoreDisplay>
                <div>{t('over.points')}</div>
              </div>

              {isNewHighScore && (
                <>
                  <Congratulations>
                    {highScoreMessage}
                  </Congratulations>
                  {scopeSummary && (
                    <ScopePill>
                      {scopesToSave.length === 2 ? '🌍' : scopesToSave[0] === 'global' ? '🌍' : '🏡'} {scopeSummary}
                    </ScopePill>
                  )}
                  {!winnerName && !hasSubmittedScore && (
                    <>
                      <Input
                        type="text"
                        placeholder={t('over.namePlaceholder')}
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        maxLength={20}
                        onKeyDown={(e) => e.key === 'Enter' && void handleSubmitScore()}
                      />
                      <ButtonContainer style={{ marginBottom: 10 }}>
                        <Button $variant="primary" onClick={() => void handleSubmitScore()} disabled={hasSubmittedScore || isSavingScore}>
                          {isSavingScore ? t('over.saving') : t('over.save')}
                        </Button>
                      </ButtonContainer>
                    </>
                  )}
                  {winnerName && (
                    <div style={{ color: '#4CAF50', fontWeight: 'bold', marginTop: '10px', fontSize: '14px', marginBottom: '20px', textAlign: 'center' }}>
                      ✓ Resultatet är sparat för {winnerName}
                    </div>
                  )}
                </>
              )}

              {hasSubmittedScore && (
                <Congratulations>
                  {t('over.saved')}
                </Congratulations>
              )}

              <PositionHint>
                🏡 Din lokala placering: <strong>#{projectedLocalRank}</strong>
                {isGlobalHighScore && (
                  <>
                    {' '}• 🌍 Din globala placering: <strong>#{projectedGlobalRank}</strong>
                  </>
                )}
              </PositionHint>

              <TopThreeWrap>
                <TopThreeTitle>🏆 Topp 3 lokalt</TopThreeTitle>
                {top3Local.length === 0 && (
                  <EmptyState>Ingen topplista än. Du kan bli etta.</EmptyState>
                )}
                {top3Local.map((entry, index) => (
                  <TopThreeItem key={entry.id}>
                    <div>
                      <RankBadge>#{index + 1}</RankBadge> {entry.playerName}
                    </div>
                    <strong>{entry.score} p</strong>
                  </TopThreeItem>
                ))}
              </TopThreeWrap>
            </TabPanel>
          )}

          {activeTab === 'highlights' && (
            <TabPanel>
              {highlights.length === 0 ? (
                <EmptyState>Inga höjdpunkter från den här rundan.</EmptyState>
              ) : (
                <HighlightsList>
                  {highlights.map((item, idx) => (
                    <HighlightRow key={`${item.title}-${idx}`}>
                      <div style={{ fontSize: 20, textAlign: 'center' }}>{item.icon || '✨'}</div>
                      <HighlightText>
                        <strong>{item.title}</strong>
                        {item.subtitle && <span style={{ color: '#666', fontSize: 13 }}>{item.subtitle}</span>}
                      </HighlightText>
                    </HighlightRow>
                  ))}
                </HighlightsList>
              )}
            </TabPanel>
          )}

          {activeTab === 'highscores' && (
            <TabPanel>
              <ScoreScopeTabs>
                <ScopeTabButton $active={scoreScope === 'local'} onClick={() => setScoreScope('local')}>🏡 Lokal</ScopeTabButton>
                <ScopeTabButton $active={scoreScope === 'global'} onClick={() => setScoreScope('global')}>🌍 Global</ScopeTabButton>
              </ScoreScopeTabs>

              {scoreList.length === 0 ? (
                <EmptyState>
                  {scoreScope === 'global' && isSyncingGlobal ? 'Laddar global topplista...' : 'Inga resultat i den här topplistan än.'}
                </EmptyState>
              ) : (
                <TopThreeWrap style={{ marginTop: 0 }}>
                  {scoreList.slice(0, 12).map((entry, index) => (
                    <TopThreeItem key={entry.id}>
                      <div>
                        <RankBadge>#{index + 1}</RankBadge> {entry.playerName}
                      </div>
                      <strong>{entry.score} p</strong>
                    </TopThreeItem>
                  ))}
                </TopThreeWrap>
              )}
            </TabPanel>
          )}
        </ModalContent>
        
        <ModalFooter>
          <ButtonContainer>
            <Button $variant="primary" onClick={handleNewGame}>
              {t('over.newGame')}
            </Button>
            <Button onClick={handleViewHighScores}>
              {t('over.fullList')}
            </Button>
            <Button onClick={() => void handleShare()}>
              {shareCopied ? t('over.shareCopied') : t('over.share')}
            </Button>
          </ButtonContainer>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};

export default GameOverModal;
